import { useEffect, useState } from "react";
import { db, auth } from "../FirebaseConfig";
import {
  collection,
  query,
  orderBy,
  onSnapshot,
  doc,
  getDoc,
  where,
} from "firebase/firestore";
import { onAuthStateChanged } from "firebase/auth";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import { FaUserCircle } from "react-icons/fa";

dayjs.extend(relativeTime);

export default function RecentHistory() {
  const [convos, setConvos] = useState([]);
  const [currentUser, setCurrentUser] = useState(null);
  const [isLoadingChats, setIsLoadingChats] = useState(true);
  const navigate = useNavigate();

  // ✅ Watch user login state
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setCurrentUser(user);
    });
    return () => unsubscribe();
  }, []);

  // ✅ Fetch recent chats from messages
  useEffect(() => {
    if (!currentUser?.uid || !db) {
      setIsLoadingChats(false);
      return;
    }

    setIsLoadingChats(true);

    const q = query(
      collection(db, "messages"),
      where("participants", "array-contains", currentUser.uid),
      orderBy("timestamp", "desc")
    );
    const unsub = onSnapshot(
      q,
      async (snapshot) => {
        const conversationsMap = new Map();
        const participantIdsToFetch = new Set();
    
        console.log("🔍 Total messages found:", snapshot.docs.length);
    
        snapshot.docs.forEach((d, i) => {
          const msg = d.data();
          console.log(`📦 Message ${i + 1}`, msg);
    
          const otherId = msg.participants.find((id) => id !== currentUser.uid);
    
          console.log("🧠 Found otherId:", otherId, "| currentUser:", currentUser.uid);
    
          if (otherId && !conversationsMap.has(otherId)) {
            conversationsMap.set(otherId, {
              otherId,
              lastMessageText: msg.text,
              lastMessageTimestamp: msg.timestamp?.toDate?.() || new Date(),
              messageId: d.id,
              participantName: "Loading...",
              participantAvatar: null,
            });
            participantIdsToFetch.add(otherId);
          }
        });
    
        console.log("👥 Unique participant IDs to fetch:", Array.from(participantIdsToFetch));
    
        const fetchedParticipants = new Map();
        for (const pId of Array.from(participantIdsToFetch)) {
          try {
            let snap = await getDoc(doc(db, "profiles", pId));
            if (!snap.exists()) {
              console.log(`👀 No profile found in 'profiles' for ${pId}, checking 'users'...`);
              snap = await getDoc(doc(db, "users", pId));
            }
            if (snap.exists()) {
              console.log(`✅ Fetched profile for ${pId}:`, snap.data());
              fetchedParticipants.set(pId, {
                name: snap.data().name || "Unknown User",
                profilePic: snap.data().profilePic || null,
              });
            } else {
              console.warn(`❌ No profile found for ${pId} in either 'profiles' or 'users'.`);
              fetchedParticipants.set(pId, {
                name: "Unknown User",
                profilePic: null,
              });
            }
          } catch (error) {
            console.error(`🔥 Error fetching profile for ${pId}:`, error);
          }
        }
    
        const finalConvos = Array.from(conversationsMap.values()).map((convo) => {
          const participantDetails = fetchedParticipants.get(convo.otherId);
          return {
            ...convo,
            participantName: participantDetails?.name,
            participantAvatar: participantDetails?.profilePic,
          };
        });
    
        console.log("🧾 Final conversations to render:", finalConvos);
        setConvos(finalConvos);
        setIsLoadingChats(false);
      },
      (error) => {
        console.error("🚨 Error listening to recent chats:", error);
        setIsLoadingChats(false);
      }
    );
    

    return () => unsub();
  }, [currentUser, db]);

  const openChat = (otherId) => {
    navigate(`/chat/${otherId}`);
  };

  if (!currentUser) {
    return (
      <div className="max-w-md mx-auto p-4 flex items-center justify-center h-[calc(100vh-20px)]">
        <p className="text-gray-600">Please log in to see your recent chats.</p>
      </div>
    );
  }

  if (isLoadingChats) {
    return (
      <div className="max-w-md mx-auto p-4 flex items-center justify-center h-[calc(100vh-20px)]">
        <p className="text-gray-500">Loading recent chats...</p>
      </div>
    );
  }

  return (
    <div className="max-w-md mx-auto p-4 bg-transparent text-white min-h-screen">
      <h2 className="text-2xl text-yellow-400 font-bold mb-6 text-center">Recent Chats</h2>

      {convos.length === 0 ? (
        <div className="flex flex-col items-center justify-center h-[calc(100vh-150px)]">
          <p className="text-gray-400 text-lg">No chats yet. Start a new conversation!</p>
        </div>
      ) : (
        <ul className="space-y-4">
          {convos.map((c) => (
            <li
              key={c.messageId}
              onClick={() => openChat(c.otherId)}
              className="flex items-center p-4 bg-white/10 hover:bg-yellow-500 hover:text-gray-900 rounded-xl border border-yellow-600 shadow-lg transition-all duration-200 ease-in-out cursor-pointer"
            >
              {c.participantAvatar ? (
                <img
                  src={c.participantAvatar}
                  alt={c.participantName}
                  className="w-12 h-12 rounded-full object-cover mr-4 border-2 border-yellow-400 shrink-0"
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = "https://i.pinimg.com/564x/c0/73/f7/c073f7c24358ca24d7ee14cbff657a0e.jpg";
                  }}
                />
              ) : (
                <FaUserCircle className="w-12 h-12 text-gray-400 mr-4 shrink-0" />
              )}

              <div className="flex flex-col flex-grow min-w-0">
                <div className="flex justify-between items-baseline mb-1">
                  <span className="text-lg font-semibold truncate flex-grow pr-2">
                    {c.participantName || "Unknown User"}
                  </span>
                  <span className="text-xs text-gray-300 whitespace-nowrap">
                    {dayjs(c.lastMessageTimestamp).fromNow()}
                  </span>
                </div>
                <p className="text-sm text-gray-300 truncate">{c.lastMessageText}</p>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
